<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import Dialog from 'primevue/dialog';
import MultiSelect from 'primevue/multiselect';
import Select from 'primevue/select';
import Button from 'primevue/button';

const props = defineProps({
    visible: {
        type: Boolean,
        default: false,
    },
    availableLanguages: {
        type: Array as () => Array<{ code: string; name: string }>,
        required: true,
    },
    hasDefaultTexts: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits([
    "update:visible",
    "export"
]);

const { t } = useI18n();

// Export dialog state
const selectedLanguages = ref<string[]>([]);
const exportMode = ref<'profile' | 'questionnaire' | 'texts'>(props.hasDefaultTexts ? 'texts' : 'profile');

// Watch for changes to hasDefaultTexts prop
watch(() => props.hasDefaultTexts, (newValue) => {
    exportMode.value = newValue ? 'texts' : 'profile';
}, { immediate: true });

// Options for data type selection
const dataTypeOptions = computed(() => {
    if (props.hasDefaultTexts) {
        // For standalone products, only show texts option
        return [{ label: t('textMaintenance.exportDialog.dataTypes.texts'), value: 'texts' }];
    } else {
        // For profile/questionnaire products, show both options
        return [
            { label: t('textMaintenance.exportDialog.dataTypes.profile'), value: 'profile' },
            { label: t('textMaintenance.exportDialog.dataTypes.questionnaire'), value: 'questionnaire' },
        ];
    }
});

// Computed properties for language selection
const languageOptions = computed(() => 
    props.availableLanguages.map(lang => ({
        label: `${lang.name} (${lang.code})`,
        value: lang.code
    }))
);

const allLanguageCodes = computed(() => 
    props.availableLanguages.map(lang => lang.code)
);

const isAllSelected = computed(() => 
    selectedLanguages.value.length === props.availableLanguages.length
);

// Methods
function selectAllLanguages() {
    selectedLanguages.value = [...allLanguageCodes.value];
}

function deselectAllLanguages() {
    selectedLanguages.value = [];
}

function confirmExport() {
    if (selectedLanguages.value.length === 0) {
        return;
    }
    
    emit("export", {
        languages: selectedLanguages.value,
        mode: exportMode.value
    });
    
    handleClose();
}

function handleClose() {
    emit("update:visible", false);
    // Reset form state
    selectedLanguages.value = [];
    exportMode.value = props.hasDefaultTexts ? 'texts' : 'profile';
}
</script>

<template>
    <Dialog
        :visible="visible"
        @update:visible="$emit('update:visible', $event)"
        modal
        :header="t('textMaintenance.exportDialog.title')"
        :style="{ width: '500px' }"
        data-cy="text-maintenance_export-dialog"
    >
        <div class="flex flex-col gap-6">
            <!-- Language Selection -->
            <div>
                <label for="languageSelect" class="block text-sm font-medium mb-2">
                    {{ t('textMaintenance.exportDialog.languages') }}
                </label>
                <MultiSelect
                    id="languageSelect"
                    v-model="selectedLanguages"
                    :options="languageOptions"
                    optionLabel="label"
                    optionValue="value"
                    :placeholder="t('textMaintenance.exportDialog.languagesPlaceholder')"
                    class="w-full"
                    :maxSelectedLabels="3"
                    data-cy="text-maintenance_export-language-select"
                />
                
                <!-- Select All / Deselect All buttons -->
                <div class="flex gap-2 mt-2">
                    <Button
                        :label="t('textMaintenance.exportDialog.selectAll')"
                        severity="secondary"
                        size="small"
                        text
                        @click="selectAllLanguages"
                        :disabled="isAllSelected"
                        data-cy="text-maintenance_export-select-all"
                    />
                    <Button
                        :label="t('textMaintenance.exportDialog.deselectAll')"
                        severity="secondary"
                        size="small"
                        text
                        @click="deselectAllLanguages"
                        :disabled="selectedLanguages.length === 0"
                        data-cy="text-maintenance_export-deselect-all"
                    />
                </div>
            </div>

            <!-- Data Type Selection (only show for profile/questionnaire products) -->
            <div v-if="!hasDefaultTexts">
                <label for="dataTypeSelect" class="block text-sm font-medium mb-2">
                    {{ t('textMaintenance.exportDialog.dataType') }}
                </label>
                <Select
                    id="dataTypeSelect"
                    v-model="exportMode"
                    :options="dataTypeOptions"
                    optionLabel="label"
                    optionValue="value"
                    class="w-full"
                    data-cy="text-maintenance_export-data-type-select"
                />
            </div>

            <!-- Validation message -->
            <div v-if="selectedLanguages.length === 0" class="p-4 bg-yellow-50 border-l-4 border-yellow-400">
                <div class="flex">
                    <div class="shrink-0">
                        <i class="pi pi-exclamation-triangle text-yellow-500"></i>
                    </div>
                    <div class="ml-4">
                        <p class="text-sm text-yellow-700" data-cy="text-maintenance_export-no-languages-warning">
                            {{ t('textMaintenance.exportDialog.noLanguagesSelected') }}
                        </p>
                    </div>
                </div>
            </div>
        </div>
        
        <template #footer>
            <div class="flex justify-end gap-2">
                <Button
                    :label="t('buttons.cancel')"
                    severity="secondary"
                    @click="handleClose"
                    data-cy="text-maintenance_export-cancel-button"
                />
                <Button
                    :label="t('textMaintenance.export')"
                    icon="pi pi-download"
                    :disabled="selectedLanguages.length === 0"
                    @click="confirmExport"
                    data-cy="text-maintenance_export-confirm-button"
                />
            </div>
        </template>
    </Dialog>
</template>